import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import {
  formatAdminDashboardLocaleLabel,
  formatAdminDashboardLocaleOptionLabel,
} from "../../shared/locale/admin-dashboard-copy";

export type AdminShellNavigationItem = {
  label: string;
  to: string;
};

export type AdminShellNavigationSection = {
  items: AdminShellNavigationItem[];
  label: string;
};

export type AdminShellViewModel = {
  locale: AdminDashboardLocale;
  localeLabel: string;
  localeOptions: Array<{
    key: AdminDashboardLocale;
    label: string;
  }>;
  navigationSections: AdminShellNavigationSection[];
  shellEyebrow: string;
  shellBrand: string;
};

function toPrimaryNavigationItems(locale: AdminDashboardLocale): AdminShellNavigationItem[] {
  return locale === "ko"
    ? [
        { label: "개요", to: "/admin" },
        { label: "사용자", to: "/admin/users" },
        { label: "대화 내역", to: "/admin/conversations" },
        { label: "실기기 지표", to: "/admin/device-kpis" },
        { label: "오류 흐름", to: "/admin/failed-flows" },
        { label: "인식 감사", to: "/admin/recognition-audits" },
      ]
    : [
        { label: "Overview", to: "/admin" },
        { label: "Users", to: "/admin/users" },
        { label: "Conversations", to: "/admin/conversations" },
        { label: "Device KPIs", to: "/admin/device-kpis" },
        { label: "Failed Flows", to: "/admin/failed-flows" },
        { label: "Recognition Audits", to: "/admin/recognition-audits" },
      ];
}

function toSupportingNavigationItems(locale: AdminDashboardLocale): AdminShellNavigationItem[] {
  return locale === "ko"
    ? [
        { label: "작업", to: "/admin/jobs" },
        { label: "릴리스", to: "/admin/releases" },
        { label: "운영 정보", to: "/admin/operation-context" },
      ]
    : [
        { label: "Jobs", to: "/admin/jobs" },
        { label: "Releases", to: "/admin/releases" },
        { label: "Operation context", to: "/admin/operation-context" },
      ];
}

function toPhase2NavigationItems(locale: AdminDashboardLocale): AdminShellNavigationItem[] {
  return locale === "ko"
    ? [
        { label: "출시 플래그", to: "/admin/phase2/flags" },
        { label: "공개 사례 검토", to: "/admin/phase2/cases" },
        { label: "증상 태그 검토", to: "/admin/phase2/tags" },
        { label: "커뮤니티 신고", to: "/admin/phase2/reports" },
        { label: "건강 정보", to: "/admin/phase2/health-content" },
      ]
    : [
        { label: "Release flags", to: "/admin/phase2/flags" },
        { label: "Public case review", to: "/admin/phase2/cases" },
        { label: "Symptom tag review", to: "/admin/phase2/tags" },
        { label: "Community reports", to: "/admin/phase2/reports" },
        { label: "Health content", to: "/admin/phase2/health-content" },
      ];
}

export function createAdminShellViewModel(locale: AdminDashboardLocale): AdminShellViewModel {
  const isKorean = locale === "ko";

  return {
    locale,
    localeLabel: formatAdminDashboardLocaleLabel(locale),
    localeOptions: [
      { key: "ko", label: formatAdminDashboardLocaleOptionLabel(locale, "ko") },
      { key: "en", label: formatAdminDashboardLocaleOptionLabel(locale, "en") },
    ],
    navigationSections: [
      {
        label: isKorean ? "주요 운영 흐름" : "Primary workflows",
        items: toPrimaryNavigationItems(locale),
      },
      {
        label: isKorean ? "Phase 2 운영" : "Phase 2 operations",
        items: toPhase2NavigationItems(locale),
      },
      {
        label: isKorean ? "보조 상세 화면" : "Supporting drill-downs",
        items: toSupportingNavigationItems(locale),
      },
    ],
    shellEyebrow: isKorean ? "PetLog 관리자" : "PetLog Admin",
    shellBrand: isKorean ? "관리자 콘솔" : "Admin Console",
  };
}
